import { SupabaseDocumentDraftRepository } from './repositories/document-drafts-repository';
import type { IDataService } from '../../interfaces/src';
import { getSupabaseClient } from './client';
import { SupabaseClientRepository } from './repositories/client-repository';
import { SupabaseRepresentativeRepository } from './repositories/representative-repository';
import { SupabaseSupplierRepository } from './repositories/supplier-repository';
import { SupabaseArticleRepository, SupabaseArticleCategoryRepository, SupabaseArticleFamilyRepository, SupabaseArticleBrandRepository } from './repositories/article-repository';
import { SupabaseSalesRepository } from './repositories/sales-repository';
import { SupabasePurchaseRepository } from './repositories/purchase-repository';
import { SupabaseStockRepository } from './repositories/stock-repository';
import { SupabaseStoreRepository } from './repositories/store-repository';
import { SupabasePaymentRepository } from './repositories/payment-repository';
import { SupabasePaymentCategoryRepository } from './repositories/payment-category-repository';
import { SupabaseRecurringPaymentRepository } from './repositories/recurring-payment-repository';
import { SupabaseTreasuryRepository } from './repositories/treasury-repository';
import { SupabaseCaisseRepository } from './repositories/caisse-repository';
import { SupabaseCheckDraftRepository } from './repositories/checks-repository';
import { SupabaseUserPreferencesRepository } from './repositories/user-preferences-repository';
import { SupabaseBusinessGoalsRepository } from './repositories/business-goals-repository';
import { SupabaseAppConfigRepository } from './repositories/app-config-repository';
import { SupabaseSettingsRepository } from './repositories/settings-repository';
import { SupabasePdfTemplateRepository } from './repositories/pdf-template-repository';
import { SupabaseDocumentPropertyRepository } from './repositories/document-property-repository';
import { SupabaseWithholdingRepository } from './repositories/withholding-repository';
import {
  SupabaseStorefrontCategoryRepository,
  SupabaseStorefrontProductRepository,
  SupabaseStoreSettingsRepository,
  SupabaseStoreThemeRepository,
  SupabaseStoreBannerRepository,
  SupabaseStorePageRepository,
  SupabaseStoreCheckoutFieldRepository,
} from './repositories/storefront-repository';
import {
  SupabaseOnlineCustomerRepository,
  SupabaseOrderAttributionRepository,
  SupabaseShipmentRepository,
  SupabaseStorefrontCheckoutRepository,
  SupabaseStorefrontCustomerAccountRepository,
  SupabaseStorefrontAnalyticsRepository,
} from './repositories/storefront-orders-repository';
import { SupabaseStorePaymentProvidersRepository } from './repositories/store-payment-providers-repository';
import {
  SupabaseAdminNotificationRepository,
  SupabaseAbandonedCartRepository,
} from './repositories/notifications-repository';
import { SupabasePushTokenRepository } from './repositories/push-tokens-repository';
import { SupabaseSessionsRepository } from './repositories/sessions-repository';
import { SupabaseUserProfileRepository } from './repositories/user-profile-repository';
import { SupabaseFormationPageRepository } from './repositories/formation-repository';
import { SupabaseResourcesRepository } from './repositories/resources-repository';
import { SupabaseDeliveryProvidersRepository } from './repositories/delivery-providers-repository';
import { SupabaseFacebookPixelsRepository } from './repositories/facebook-pixels-repository';
import { SupabaseSocialRepository } from './repositories/social-repository';
import { SupabaseMessagingRepository } from './repositories/messaging-repository';
import { SupabaseSmsRepository } from './repositories/sms-repository';
import { SupabaseSharingRepository } from './repositories/sharing-repository';
import { SupabaseTenantsRepository } from './repositories/tenants-repository';
import { SupabaseRolesRepository } from './repositories/roles-repository';
import { SupabaseTeamRepository } from './repositories/team-repository';
import { SupabaseInvitationsRepository } from './repositories/invitations-repository';
import { SupabaseDomainSyncRepository } from './repositories/domain-sync-repository';
import { SupabaseTelemetryRepository } from './repositories/telemetry-repository';
import { SupabaseSubscriptionRepository } from './repositories/subscriptions-repository';
import { SupabasePlatformAdminRepository } from './repositories/platform-admin-repository';
import { SupabaseCrmRepository } from './repositories/crm-repository';
import { SupabaseProCustomerRepository } from './repositories/pro-customer-repository';
import { SupabaseProArticlePricingRepository } from './repositories/pro-article-pricing-repository';
import { SupabaseProSettingsRepository } from './repositories/pro-settings-repository';
import { SupabaseProCategoryRepository } from './repositories/pro-category-repository';
import { SupabaseProClientRepository } from './repositories/pro-client-repository';
import { SupabaseProDevisRepository } from './repositories/pro-devis-repository';
import { SupabaseProCommandeRepository } from './repositories/pro-commande-repository';
import { SupabaseProWalletRepository } from './repositories/pro-wallet-repository';
import { SupabaseProFavoriteRepository } from './repositories/pro-favorite-repository';
import { SupabaseProNotificationRepository } from './repositories/pro-notification-repository';
import { SupabaseProProductRequestRepository } from './repositories/pro-product-request-repository';
import { SupabaseArticleVariantRepository } from './repositories/article-variant-repository';
import { SupabaseArticleAttributePresetRepository } from './repositories/article-attribute-preset-repository';
import { SupabaseAiRepository } from './repositories/ai-repository';
import { SupabaseAiCreditsRepository } from './repositories/ai-credits-repository';
import { SupabaseAgentRepository } from './repositories/agent-repository';
import { SupabaseSupportRepository } from './repositories/support-repository';

// Single Supabase project, multi-tenant via `tenant_id` on every business
// table. The legacy two-tier (master config + per-tenant DB) split is gone;
// the constructor still accepts tenant URL/key for backward-compat callers
// but the second client falls back to master in practice.
export class SupabaseDataService implements IDataService {
  public readonly documentDrafts;
  public readonly clients;
  public readonly representatives;
  public readonly suppliers;
  public readonly articleCategories;
  public readonly articleFamilies;
  public readonly articleBrands;
  public readonly articles;
  public readonly articleVariants;
  public readonly articleAttributePresets;
  public readonly sales;
  public readonly purchases;
  public readonly stores;
  public readonly stock;
  public readonly payments;
  public readonly paymentCategories;
  public readonly recurringPayments;
  public readonly treasury;
  public readonly caisse;
  public readonly checks;
  public readonly userPreferences;
  public readonly businessGoals;
  public readonly appConfig;
  public readonly settings;
  public readonly pdfTemplates;
  public readonly documentProperties;
  public readonly withholding;
  // Storefront
  public readonly storefrontCategories;
  public readonly storefrontProducts;
  public readonly onlineCustomers;
  public readonly storeSettings;
  public readonly storeTheme;
  public readonly storeBanners;
  public readonly storePages;
  public readonly storeCheckoutFields;
  public readonly orderAttribution;
  public readonly shipments;
  public readonly storefrontCheckout;
  // Per-tenant payment gateways (Flouci) — merchant config + storefront read
  public readonly storePaymentProviders;
  public readonly storefrontCustomerAccount;
  public readonly storefrontAnalytics;
  public readonly adminNotifications;
  public readonly abandonedCarts;
  // Device push tokens for the signed-in user (server-side push fan-out)
  public readonly pushTokens;
  public readonly sessions;
  // Identité personnelle du compte (photo, couverture, téléphone, fonction)
  public readonly userProfile;
  // Formations (in-app training)
  public readonly formationPages;
  // Ressources
  public readonly resources;
  // Delivery providers (Intigo + future carriers)
  public readonly deliveryProviders;
  // Facebook pixels + Conversions API (server-side events)
  public readonly facebookPixels;
  // Social publishing (Facebook Page + Instagram, BYO-account)
  public readonly social;
  // Unified customer inbox (Messenger + Instagram + WhatsApp)
  public readonly messaging;
  // Outbound transactional / consented marketing SMS
  public readonly sms;
  // Document share links (short /d/{code} URLs)
  public readonly sharing;
  // AI assistance (OpenAI proxy via Edge Function)
  public readonly ai;
  // AI credit wallet (whole-credit balance shared by the tenant)
  public readonly aiCredits;
  // Agent conversationnel (proxy gesty-agent + journal des exécutions)
  public readonly agent;
  // Support chat (tenant ⇄ Gesty support)
  public readonly support;
  // Multi-tenant — auth + tenants metadata always on master
  public readonly tenants;
  // Team / user management — master DB (tenant_members, roles, invitations)
  public readonly team;
  public readonly invitations;
  public readonly roles;
  // Domain sync queue (Cloudflare Pages binding state)
  public readonly domainSync;
  public readonly telemetry;
  // Subscriptions / billing (always master)
  public readonly subscriptions;
  // Platform admin console (admin.gesty.tn)
  public readonly platformAdmin;
  // CRM prospection (platform admin, master DB)
  public readonly crm;
  // ProSpace
  public readonly proCustomers;
  public readonly proArticlePricing;
  public readonly proSettings;
  public readonly proCategories;
  public readonly proClients;
  public readonly proDevis;
  public readonly proCommandes;
  public readonly proWallet;
  public readonly proFavorites;
  public readonly proNotifications;
  public readonly proProductRequests;

  constructor(
    masterUrl: string,
    masterAnonKey: string,
    tenantUrl?: string,
    tenantAnonKey?: string,
    /** Pass-through to createClient — mobile uses this to wire AsyncStorage. */
    clientOptions?: import('@supabase/supabase-js').SupabaseClientOptions<'public'>,
  ) {
    const master = getSupabaseClient(masterUrl, masterAnonKey, clientOptions);
    const tenant =
      tenantUrl && tenantAnonKey
        ? getSupabaseClient(tenantUrl, tenantAnonKey, clientOptions)
        : master;

    // Master-scoped (auth-anchored data + per-tenant config tables that
    // sit alongside the user account on master).
    this.userPreferences = new SupabaseUserPreferencesRepository(master);
    this.appConfig = new SupabaseAppConfigRepository(master);
    this.settings = new SupabaseSettingsRepository(master);
    this.pdfTemplates = new SupabasePdfTemplateRepository(master);
    this.documentProperties = new SupabaseDocumentPropertyRepository(master);

    // Tenant-scoped (business data only)
    this.clients = new SupabaseClientRepository(tenant);
    // Business data, not account config: the revenue target belongs to the
    // company whose invoices it is measured against, so it lives with them.
    this.businessGoals = new SupabaseBusinessGoalsRepository(tenant);
    this.representatives = new SupabaseRepresentativeRepository(tenant);
    this.suppliers = new SupabaseSupplierRepository(tenant);
    this.articleCategories = new SupabaseArticleCategoryRepository(tenant);
    this.articleFamilies = new SupabaseArticleFamilyRepository(tenant);
    this.articleBrands = new SupabaseArticleBrandRepository(tenant);
    this.articles = new SupabaseArticleRepository(tenant);
    this.articleVariants = new SupabaseArticleVariantRepository(tenant);
    this.articleAttributePresets = new SupabaseArticleAttributePresetRepository(tenant);
    this.documentDrafts = new SupabaseDocumentDraftRepository(tenant);
    this.sales = new SupabaseSalesRepository(tenant);
    this.purchases = new SupabasePurchaseRepository(tenant);
    this.stores = new SupabaseStoreRepository(tenant);
    this.stock = new SupabaseStockRepository(tenant);
    this.payments = new SupabasePaymentRepository(tenant);
    this.paymentCategories = new SupabasePaymentCategoryRepository(tenant);
    // Reçoit le repo paiements : approuver une échéance doit produire un
    // paiement STRICTEMENT identique à une saisie manuelle (numérotation
    // légale, lien caisse, mouvement de tiroir).
    this.recurringPayments = new SupabaseRecurringPaymentRepository(tenant, this.payments);
    this.treasury = new SupabaseTreasuryRepository(tenant);
    this.caisse = new SupabaseCaisseRepository(tenant);
    this.checks = new SupabaseCheckDraftRepository(tenant);
    this.withholding = new SupabaseWithholdingRepository(tenant);

    // Storefront catalog data is tenant-scoped, but store settings are
    // master-scoped because hostname routing resolves a tenant from the
    // master `tenants` table and then reads `store_settings` by that id.
    // If a storefront deployment provides tenant DB env vars, reading
    // store_settings from the tenant DB makes valid hosts render "Store not
    // found" after `tenants.getByHostname()` succeeds.
    this.storefrontCategories = new SupabaseStorefrontCategoryRepository(tenant);
    this.storefrontProducts = new SupabaseStorefrontProductRepository(tenant);
    this.onlineCustomers = new SupabaseOnlineCustomerRepository(tenant);
    this.storeSettings = new SupabaseStoreSettingsRepository(master);
    this.storeTheme = new SupabaseStoreThemeRepository(master);
    this.storeBanners = new SupabaseStoreBannerRepository(tenant);
    this.storePages = new SupabaseStorePageRepository(tenant);
    this.storeCheckoutFields = new SupabaseStoreCheckoutFieldRepository(tenant);
    this.orderAttribution = new SupabaseOrderAttributionRepository(tenant);
    this.shipments = new SupabaseShipmentRepository(tenant);
    this.storefrontCheckout = new SupabaseStorefrontCheckoutRepository(tenant);
    // Master like store_settings/delivery_providers: gateway credentials are
    // per-tenant CONFIG resolved alongside hostname routing, not business data.
    this.storePaymentProviders = new SupabaseStorePaymentProvidersRepository(master);
    this.storefrontCustomerAccount = new SupabaseStorefrontCustomerAccountRepository(tenant);
    this.storefrontAnalytics = new SupabaseStorefrontAnalyticsRepository(tenant);
    this.adminNotifications = new SupabaseAdminNotificationRepository(tenant);
    this.abandonedCarts = new SupabaseAbandonedCartRepository(tenant);
    this.pushTokens = new SupabasePushTokenRepository(tenant);
    // Master comme team/roles/subscriptions : le registre d'appareils est de
    // la plomberie d'identité (auth.users / auth.sessions), pas de la donnée
    // métier tenant-scopée.
    this.sessions = new SupabaseSessionsRepository(master);
    // Même raison : `user_profiles` est ancrée sur auth.users et n'a pas de
    // `tenant_id` — elle suit la personne quand elle change de société.
    this.userProfile = new SupabaseUserProfileRepository(master);

    // Formations (in-app training) — tenant-scoped.
    this.formationPages = new SupabaseFormationPageRepository(tenant);

    // Ressources — bibliothèque interne de documents, tenant-scopée.
    this.resources = new SupabaseResourcesRepository(tenant);

    // Delivery providers — master-scopés. Suivent la convention des autres
    // configurations (company_settings, module_settings, pdf_templates) qui
    // vivent sur master. La clé API Intigo est une donnée de config, pas
    // de la donnée transactionnelle.
    this.deliveryProviders = new SupabaseDeliveryProvidersRepository(master);
    this.facebookPixels = new SupabaseFacebookPixelsRepository(master);
    this.social = new SupabaseSocialRepository(master);
    this.messaging = new SupabaseMessagingRepository(master);
    this.sms = new SupabaseSmsRepository(master);
    // Share links follow the shared documents (sales/purchases) — tenant DB.
    this.sharing = new SupabaseSharingRepository(tenant);
    // AI proxy — invokes the `ai-generate` Edge Function with the caller's
    // JWT (verify_jwt = true). No DB scope, so master/tenant is irrelevant.
    this.ai = new SupabaseAiRepository(master);
    this.aiCredits = new SupabaseAiCreditsRepository(master);
    // Même famille que l'IA : la fonction edge `gesty-agent` débite le même
    // portefeuille, et le journal d'exécutions vit à côté de ce portefeuille.
    this.agent = new SupabaseAgentRepository(master);
    this.support = new SupabaseSupportRepository(tenant);
    this.tenants = new SupabaseTenantsRepository(master);
    // Team / user management — master DB. Roles and tenant_members are
    // joined per-tenant via RLS; the SECURITY DEFINER RPCs gate writes.
    this.roles = new SupabaseRolesRepository(master);
    this.team = new SupabaseTeamRepository(master);
    this.invitations = new SupabaseInvitationsRepository(master);
    this.domainSync = new SupabaseDomainSyncRepository(master);
    // Navigation telemetry — the rollup sits next to `tenants` on master,
    // and the RPC resolves the workspace through current_user_tenant_id().
    this.telemetry = new SupabaseTelemetryRepository(master);
    this.subscriptions = new SupabaseSubscriptionRepository(master);
    this.platformAdmin = new SupabasePlatformAdminRepository(master);
    // CRM prospection — données plateforme (pipeline Khalil), RLS is_admin().
    this.crm = new SupabaseCrmRepository(master);

    // ProSpace — tenant-scoped (les pro_customers vivent dans la même DB
    // partagée master/tenant que tout le reste, scopés par tenant_id + RLS).
    this.proCustomers = new SupabaseProCustomerRepository(tenant);
    this.proArticlePricing = new SupabaseProArticlePricingRepository(tenant);
    this.proSettings = new SupabaseProSettingsRepository(tenant);
    this.proCategories = new SupabaseProCategoryRepository(tenant);
    this.proClients = new SupabaseProClientRepository(tenant);
    this.proDevis = new SupabaseProDevisRepository(tenant);
    this.proCommandes = new SupabaseProCommandeRepository(tenant);
    this.proWallet = new SupabaseProWalletRepository(tenant);
    this.proFavorites = new SupabaseProFavoriteRepository(tenant);
    this.proNotifications = new SupabaseProNotificationRepository(tenant);
    this.proProductRequests = new SupabaseProProductRequestRepository(tenant);
  }
}
